import React, { useState, useEffect } from "react";
import { McModal } from "@maersk-global/mds-react-wrapper/components-core/mc-modal";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McLoadingIndicator } from "@maersk-global/mds-react-wrapper/mc-loading-indicator";
import { McNotification } from "@maersk-global/mds-react-wrapper";
import axios from "axios";
import { API_URL } from "../../../services/api-config";
import { useStore } from "../../../store/store.context";
import DOMPurify from "dompurify";

// PDF Styles - separated from inline styles for better maintainability
const pdfStyles = `
  @media print {
    body { margin: 0; }
    .no-print { display: none !important; }
  }
  
  body {
    font-family: "Maersk Text", Arial, sans-serif;
    margin: 20px;
    line-height: 1.4;
  }
  
  .header {
    text-align: center;
    margin-bottom: 30px;
    border-bottom: 2px solid #333;
    padding-bottom: 20px;
  }
  
  .header h1 {
    color: #333;
    margin: 0 0 10px 0;
  }
  
  .header .subtitle {
    color: #666;
    font-size: 14px;
  }
  
  .email-card {
    border: 1px solid #ddd;
    margin-bottom: 20px;
    padding: 15px;
    border-radius: 8px;
    page-break-inside: avoid;
  }
  
  .email-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    border-bottom: 1px solid #eee;
    padding-bottom: 10px;
  }
  
  .email-header h3 {
    margin: 0;
    color: #333;
  }
  
  .email-meta {
    text-align: right;
  }
  
  .template-badge {
    background-color: #e8f4f8;
    color: #0066cc;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: bold;
  }
  
  .date-time {
    font-size: 12px;
    color: #666;
    margin-top: 5px;
  }
  
  .email-details {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 15px;
  }
  
  .email-details td {
    padding: 8px;
    border: 1px solid #eee;
    vertical-align: top;
  }
  
  .email-details td:first-child {
    background-color: #f8f9fa;
    width: 150px;
    font-weight: bold;
  }
  
  .email-body {
    margin-top: 15px;
  }
  
  .email-body h4 {
    margin: 0 0 10px 0;
    color: #333;
  }
  
  .email-content {
    background-color: #f8f8f8;
    padding: 10px;
    border-radius: 4px;
    border: 1px solid #ddd;
    white-space: pre-wrap;
    font-family: monospace;
    font-size: 12px;
  }
  
  .footer {
    text-align: center;
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid #ddd;
    font-size: 12px;
    color: #666;
  }
`;

const EmailHistoryModal = ({ 
  isOpen, 
  onClose, 
  caseNumber 
}) => {
  const { store } = useStore();
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);

  // BEGIN: Initial content from MessagesModal.js (rendered first)
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesData, setMessagesData] = useState(null);
  const [messagesExporting, setMessagesExporting] = useState(false);

  // Helpers from MessagesModal.js
  const escapeHtml = (unsafe) => {
    if (unsafe == null) return '';
    return String(unsafe)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };


const htmlToText = (html) => {
  if (!html || typeof html !== "string") return "";

  try {
    const clean = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ["br"],
      ALLOWED_ATTR: []
    });
    const doc = new DOMParser().parseFromString(clean, "text/html");
    let text = doc.body.innerText || "";

    if (/Subject:/i.test(text)) {
      text = text.replace(
        /(Subject:[^\n]*)(This message was sent from outside of your organization\.\s*Please do not click links or open attachments unless you recognize the source of this email and know the content is safe\.)/i,
        "$1\n$2\n\n"
      );

      // Format headers ONLY for email headers
      text = text
        .replace(/From:\s*/gi, '\nFrom: ')
        .replace(/Sent:\s*/gi, '\nSent: ')
        .replace(/To:\s*/gi, '\nTo: ')
        .replace(/Cc:\s*/gi, '\nCc: ')
        .replace(/Subject:\s*/gi, '\nSubject: ')
        .replace(/Received:\s*/gi, '\nReceived: ');
    }

    text = text
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    return text;

  } catch (error) {
    console.error("htmlToText error:", error);
    return "";
  }
};


  const exportMessagesAsPDF = async (targetCaseNumber, messages = []) => {
    if (!messages || messages.length === 0) return;
    if (!targetCaseNumber) return;
    try {
      const parts = [];
      parts.push(`<h2>Case ${escapeHtml(targetCaseNumber)} - Exported Messages</h2>`);
      messages.forEach((m) => {
        parts.push('<div class="msg">');
        const bodyText = htmlToText(String(m.body ?? ''));
        parts.push(`<div class="body">${escapeHtml(bodyText).replace(/\n/g, '<br/>')}</div>`);
        const received = m.receive_timestamp ? m.receive_timestamp.replace('T', ' ').split('.')[0] : (m.createddate ? m.createddate.replace('T', ' ').split('.')[0] : '');
        parts.push(`<div class="footer"><em>Received: ${escapeHtml(received)}</em></div>`);
        parts.push('</div>');
      });
      const style = '<style>body{font-family: Arial, Helvetica, sans-serif; padding:20px; color:#111} .msg{border:1px solid #e6e6e6;padding:12px;border-radius:6px;margin-bottom:12px;} .hdr{font-size:13px;color:#333;margin-bottom:6px;} .body{font-size:12px;color:#444;white-space:pre-wrap;margin-top:6px;} .footer{font-size:11px;color:#777;margin-top:8px;}</style>';
      const fullHtml = '<!doctype html><html><head><meta charset="utf-8"><title>Exported Emails</title>' + style + '</head><body>' + parts.join('') + '</body></html>';
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.left = '-10000px';
      iframe.style.top = '0';
      iframe.style.width = '800px';
      iframe.style.height = '1000px';
      iframe.setAttribute('aria-hidden', 'true');
      iframe.srcdoc = fullHtml;
      document.body.appendChild(iframe);
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Iframe load timed out')), 10000);
        iframe.onload = () => { clearTimeout(timeout); resolve(); };
      });
      await new Promise(async (resolve, reject) => {
        try {
          const iDoc = iframe.contentDocument || iframe.contentWindow.document;
          let bundleCode = window.__html2pdf_bundle_code;
          if (!bundleCode) {
            const bundleUrl = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
            const resp = await fetch(bundleUrl);
            if (!resp.ok) throw new Error(`Failed to fetch html2pdf bundle: ${resp.status}`);
            bundleCode = await resp.text();
            try { window.__html2pdf_bundle_code = bundleCode; } catch (e) {}
          }
          const script = iDoc.createElement('script');
          script.type = 'text/javascript';
          script.text = bundleCode;
          iDoc.head.appendChild(script);
          const maxWait = 5000;
          const start = Date.now();
          const check = () => {
            try {
              if (iframe.contentWindow && typeof iframe.contentWindow.html2pdf === 'function') return resolve();
            } catch (e) {}
            if (Date.now() - start > maxWait) return reject(new Error('html2pdf did not initialize inside iframe'));
            setTimeout(check, 50);
          };
          check();
        } catch (e) {
          reject(e);
        }
      });
      const opt = { margin: 10, filename: `Case-${targetCaseNumber}-Messages.pdf`, image: { type: 'jpeg', quality: 0.98 }, html2canvas: { scale: 2 }, jsPDF: { unit: 'pt', format: 'a4', orientation: 'portrait' } };
      const blobPromise = new Promise((resolve, reject) => {
        let settled = false;
        const onMessage = (e) => {
          if (e.source !== iframe.contentWindow) return;
          const data = e.data || {};
          if (data && data.type === 'html2pdf:blob' && data.blob) {
            if (settled) return;
            settled = true;
            window.removeEventListener('message', onMessage);
            resolve(data.blob);
          } else if (data && data.type === 'html2pdf:error') {
            if (settled) return;
            settled = true;
            window.removeEventListener('message', onMessage);
            reject(new Error(data.message || 'Unknown error in iframe'));
          }
        };
        window.addEventListener('message', onMessage);
        setTimeout(() => {
          if (settled) return;
          settled = true;
          window.removeEventListener('message', onMessage);
          reject(new Error('Timed out waiting for PDF blob from iframe'));
        }, 20000);
      });
      const iDoc = iframe.contentDocument || iframe.contentWindow.document;
      const genScript = iDoc.createElement('script');
      genScript.type = 'text/javascript';
      genScript.text = "(function(){try{var opt = " + JSON.stringify(opt) + "; html2pdf().from(document.body).set(opt).toPdf().get('pdf').then(function(pdf){ try{ var blob = pdf.output('blob'); parent.postMessage({type:'html2pdf:blob', blob: blob}, '*'); }catch(e){ parent.postMessage({type:'html2pdf:error', message: e && e.message ? e.message : String(e)}, '*'); } }).catch(function(err){ parent.postMessage({type:'html2pdf:error', message: err && err.message ? err.message : String(err)}, '*'); }); }catch(e){ parent.postMessage({type:'html2pdf:error', message: e && e.message ? e.message : String(e)}, '*'); }})();";
      iDoc.body.appendChild(genScript);
      const blob = await blobPromise;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = opt.filename || `Case-${targetCaseNumber}-Messages.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      try { document.body.removeChild(iframe); } catch (e) {}
    } catch (err) {
      console.error('Export Emails failed', err);
      alert('Failed to export emails: ' + (err.message || String(err)));
    }
  };
  // END: Initial content from MessagesModal.js

  // Fetch email logs when modal opens
  useEffect(() => {
    if (isOpen && caseNumber) {
      fetchEmailLogs();
    }
  }, [isOpen, caseNumber]);

  // Fetch messages (from MessagesModal) when modal opens
  useEffect(() => {
    let mounted = true;
    const fetchMessages = async () => {
      if (!isOpen || !caseNumber) return;
      try {
        setMessagesLoading(true);
        setMessagesData(null);
        const resp = await fetch(`${API_URL}/get_case_messages?casenumber=${encodeURIComponent(caseNumber)}`);
        if (!resp.ok) {
          const text = await resp.text().catch(() => resp.statusText || 'error');
          throw new Error(`Failed to fetch messages: ${resp.status} ${text}`);
        }
        const json = await resp.json();
        if (mounted) setMessagesData({ casenumber: caseNumber, messages: json.messages || [], error: json.error });
      } catch (err) {
        if (mounted) setMessagesData({ casenumber: caseNumber, messages: [], error: err.message || String(err) });
      } finally {
        if (mounted) setMessagesLoading(false);
      }
    };
    fetchMessages();
    return () => { mounted = false; };
  }, [isOpen, caseNumber]);

  const fetchEmailLogs = async () => {
    if (!caseNumber) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(
        `${API_URL}/email-logs/${caseNumber}`,
        {
          headers: {
            "Authorization": `Bearer ${store.authToken}`,
          },
        }
      );
      
      // Extract email_logs from the deeply nested response structure
      // Response structure: data.body.email_logs.body.email_logs (array)
      const emailLogsData = response.data?.body?.email_logs?.body?.email_logs || [];
      const logs = Array.isArray(emailLogsData) ? emailLogsData : [];
      setEmailLogs(logs);
    } catch (error) {
      console.error("Error fetching email logs:", error);
      
      // Handle 500 series errors with a plain statement
      const statusCode = error.response?.status;
      if (statusCode >= 500 && statusCode < 600) {
        setError("Emails History from the user does not exist");
      } else {
        setError(error.response?.data?.detail || "Failed to fetch email logs");
      }
      
      setEmailLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const exportToPDF = () => {
    if (!emailLogs || emailLogs.length === 0) {
      setNotification({
        type: "error",
        message: "No email data to export"
      });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    try {
      // Create HTML content for PDF
      const htmlContent = generateHTMLReport();
      
      // Create a new window with the HTML content
      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      // Wait for content to load, then trigger print dialog
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 500);

      setNotification({
        type: "success",
        message: "PDF export initiated. Use your browser's print dialog to save as PDF."
      });
      setTimeout(() => setNotification(null), 4000);

    } catch (error) {
      console.error('Error generating PDF:', error);
      // Fallback to JSON export
      exportToJSON();
    }
  };

  const generateHTMLReport = () => {
    const currentDate = new Date();
    const formatDate = (dateString) => new Date(dateString).toLocaleDateString();
    const formatTime = (dateString) => new Date(dateString).toLocaleTimeString();
    
    const stripHtml = (html) => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      return tempDiv.textContent || tempDiv.innerText || '';
    };

    const emailsHTML = emailLogs.map((log, index) => `
      <div class="email-card">
        <div class="email-header">
          <h3>Email #${emailLogs.length - index}</h3>
          <div class="email-meta">
            <span class="template-badge">${log.template_id}</span>
            <div class="date-time">
              <div>${formatDate(log.created_at)}</div>
              <div>${formatTime(log.created_at)}</div>
            </div>
          </div>
        </div>
        
        <table class="email-details">
          <tr><td><strong>To:</strong></td><td>${log.customer_email || 'N/A'}</td></tr>
          <tr><td><strong>From:</strong></td><td>${log.sender_email || 'N/A'}</td></tr>
          <tr><td><strong>Language:</strong></td><td>${log.language?.toUpperCase() || 'EN'}</td></tr>
          <tr><td><strong>Triggered By:</strong></td><td>${log.triggered_by || 'N/A'}</td></tr>
          <tr><td><strong>Service:</strong></td><td>${log.triggered_service || 'N/A'}</td></tr>
          <tr><td><strong>Subject:</strong></td><td>${log.email_subject || 'N/A'}</td></tr>
          ${log.missing_fields && log.missing_fields.length > 0 ? 
            `<tr><td><strong>Missing Fields:</strong></td><td>${log.missing_fields.join(', ')}</td></tr>` : 
            ''
          }
        </table>
        
        ${log.email_body ? `
          <div class="email-body">
            <h4>Email Content:</h4>
            <div class="email-content">${stripHtml(log.email_body)}</div>
          </div>
        ` : ''}
      </div>
    `).join('');

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Email History Report - Case ${caseNumber}</title>
        <style>
          ${pdfStyles}
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Email History Report</h1>
          <h2>Case: ${caseNumber}</h2>
          <div class="subtitle">
            Generated on: ${currentDate.toLocaleDateString()} at ${currentDate.toLocaleTimeString()}<br>
            Total Emails: ${emailLogs.length}
          </div>
        </div>
        
        ${emailsHTML}
        
        <div class="footer">
          Report generated by AFR Digital Assistant | ${currentDate.toLocaleString()}
        </div>
      </body>
      </html>
    `;
  };

  const exportToJSON = () => {
    try {
      const exportData = {
        case_number: caseNumber,
        generated_at: new Date().toISOString(),
        total_emails: emailLogs.length,
        email_logs: emailLogs
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `Email_History_Case_${caseNumber}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setNotification({
        type: "success",
        message: "Email history exported as JSON file successfully!"
      });
      setTimeout(() => setNotification(null), 3000);

    } catch (error) {
      console.error('Error exporting JSON:', error);
      setNotification({
        type: "error",
        message: "Failed to export data. Please try again."
      });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  return (
    <>
      <McModal
        heading={`Email History - Case ${caseNumber}`}
        open={isOpen}
        closed={handleClose}
        width="80vw"
        height="90vh"
      >
        <div style={{ padding: '12px', minWidth: '600px', maxHeight: '75vh', overflow: 'auto' }}>
          {/* Initial content from MessagesModal.js - rendered first */}
          <div style={{ padding: '10px', marginBottom: '12px', border: '1px solid #e6e6e6', borderRadius: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>{messagesData?.casenumber ? `Client Email - Case Number - ${messagesData.casenumber}` : (caseNumber ? `Client Email - Case Number - ${caseNumber}` : '')}</div>
              <div>
                {messagesData && Array.isArray(messagesData.messages) && messagesData.messages.length > 0 && (
                  <McButton
                    label={messagesExporting ? 'Exporting...' : 'Export Emails'}
                    appearance="secondary"
                    disabled={messagesExporting}
                    click={async () => {
                      try {
                        setMessagesExporting(true);
                        await exportMessagesAsPDF(messagesData.casenumber || caseNumber, messagesData.messages);
                      } catch (err) {
                        console.error('Export failed', err);
                      } finally {
                        setMessagesExporting(false);
                      }
                    }}
                  />
                )}
              </div>
            </div>
            {messagesLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <McLoadingIndicator appearance="primary" />
                <div style={{ fontSize: 12, color: '#666' }}>Loading messages...</div>
              </div>
            ) : messagesData ? (
              <div>
                {messagesData.error ? (
                  <div style={{ color: 'red' }}>{messagesData.error}</div>
                ) : messagesData.messages && messagesData.messages.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {messagesData.messages.map((m, i) => {
                      const bodyText = htmlToText(String(m.body ?? ''));
                   return (
                        <div key={i} style={{ border: '1px solid #e6e6e6', padding: '8px', borderRadius: '4px', overflowY: 'auto', maxHeight: '180px' }}>
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '6px', whiteSpace: 'pre-wrap' }}>{bodyText}</div>
                          <div style={{ fontSize: '11px', color: '#999', marginTop: '6px' }}><em>Received: {m.receive_timestamp ? m.receive_timestamp.replace('T', ' ').split('.')[0] : (m.createddate ? m.createddate.replace('T', ' ').split('.')[0] : '')}</em></div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', color: '#666' }}>No messages available for this case.</div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: '12px', color: '#666' }}>No message details available.</div>
            )}
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <McLoadingIndicator appearance="primary" />
              <p style={{ marginTop: '12px' }}>Loading email history...</p>
            </div>
          ) : error && emailLogs.length === 0 ? (
            <div style={{ 
              padding: '10px', 
              backgroundColor: 'rgba(255, 254, 238, 1)', 
              border: '1px solid #fcc', 
              borderRadius: '4px',
              color: 'rgba(0, 0, 0, 1)',
              textAlign: 'center'
            }}>
               {error}
            </div>
          ) : emailLogs.length === 0 ? (
            <div style={{ 
              padding: '10px', 
              backgroundColor: '#f0f0f0', 
              border: '1px solid #ddd', 
              borderRadius: '8px',
              color: '#666',
              textAlign: 'center'
            }}>
              <h3 style={{ margin: '0 0 16px 0' }}>No Email History</h3>
              <p>No emails have been sent for this case yet.</p>
              <div style={{ marginTop: '12px' }}>
                <McButton
                  appearance="secondary"
                  fit="small"
                  icon="file"
                  disabled={true}
                >
                  Export to PDF
                </McButton>
                <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
                  Export will be available when emails are sent
                </p>
              </div>
            </div>
          ) : (
            <div className="email-logs-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ 
                marginBottom: '8px', 
                padding: '8px', 
                backgroundColor: '#e8f4f8', 
                borderRadius: '4px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <strong>{emailLogs.length} email{emailLogs.length !== 1 ? 's' : ''} found</strong>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <McButton
                    appearance="secondary"
                    fit="small"
                    icon="file"
                    click={exportToPDF}
                  >
                    Print/Save PDF
                  </McButton>
                  <McButton
                    appearance="secondary"
                    fit="small"
                    icon="file"
                    click={exportToJSON}
                  >
                    Export JSON
                  </McButton>
                </div>
              </div>

              {emailLogs.map((log, index) => (
                <div key={log.id} style={{
                  padding: '12px',
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  {/* Header */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '10px',
                    paddingBottom: '8px',
                    borderBottom: '1px solid #eee'
                  }}>
                    <div>
                      <h3 style={{ 
                        margin: '0 0 4px 0', 
                        color: '#333',
                        fontSize: '18px',
                        fontWeight: '600'
                      }}>
                        Email #{emailLogs.length - index}
                      </h3>
                      <span style={{ 
                        color: '#0066cc', 
                        fontSize: '14px',
                        fontWeight: '500',
                        backgroundColor: '#e8f4f8',
                        padding: '4px 8px',
                        borderRadius: '4px'
                      }}>
                        {log.template_id}
                      </span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: '#666', fontSize: '14px', fontWeight: '500' }}>
                        {new Date(log.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                      <div style={{ color: '#888', fontSize: '12px' }}>
                        {new Date(log.created_at).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </div>
                  
                  {/* Email Details Grid */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
                    gap: '10px', 
                    marginBottom: '10px' 
                  }}>
                    <div style={{ 
                      padding: '10px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '6px',
                      border: '1px solid #e9ecef'
                    }}>
                      <div style={{ fontWeight: '600', color: '#495057', fontSize: '12px', marginBottom: '4px' }}>
                        TO
                      </div>
                      <div style={{ color: '#333', fontSize: '14px', wordBreak: 'break-word' }}>
                        {log.customer_email}
                      </div>
                    </div>
                    
                    <div style={{ 
                      padding: '10px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '6px',
                      border: '1px solid #e9ecef'
                    }}>
                      <div style={{ fontWeight: '600', color: '#495057', fontSize: '12px', marginBottom: '4px' }}>
                        CC
                      </div>
                      <div style={{ color: '#333', fontSize: '14px', wordBreak: 'break-word' }}>
                        {log.sender_email}
                      </div>
                    </div>
                    
                    <div style={{ 
                      padding: '10px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '6px',
                      border: '1px solid #e9ecef'
                    }}>
                      <div style={{ fontWeight: '600', color: '#495057', fontSize: '12px', marginBottom: '4px' }}>
                        LANGUAGE
                      </div>
                      <div style={{ color: '#333', fontSize: '14px' }}>
                        {log.language?.toUpperCase() || 'EN'}
                      </div>
                    </div>
                    
                    <div style={{ 
                      padding: '10px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '6px',
                      border: '1px solid #e9ecef'
                    }}>
                      <div style={{ fontWeight: '600', color: '#495057', fontSize: '12px', marginBottom: '4px' }}>
                        TRIGGERED BY
                      </div>
                      <div style={{ color: '#333', fontSize: '14px' }}>
                        {log.triggered_by}
                      </div>
                    </div>
                  </div>
                  
                  {/* Subject */}
                  <div style={{ 
                    marginBottom: '8px',
                    padding: '8px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    borderRadius: '4px'
                  }}>
                    <div style={{ fontWeight: '600', color: '#856404', fontSize: '12px', marginBottom: '4px' }}>
                      SUBJECT
                    </div>
                    <div style={{ color: '#333', fontSize: '14px', fontWeight: '500' }}>
                      {log.email_subject}
                    </div>
                  </div>
                  
                  {/* Missing Fields */}
                  {log.missing_fields && log.missing_fields.length > 0 && (
                    <div style={{ 
                      marginBottom: '8px',
                      padding: '8px',
                      backgroundColor: '#f8d7da',
                      border: '1px solid #f5c6cb',
                      borderRadius: '4px'
                    }}>
                      <div style={{ fontWeight: '600', color: '#721c24', fontSize: '12px', marginBottom: '8px' }}>
                        MISSING FIELDS
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {log.missing_fields.map((field, fieldIndex) => (
                          <span 
                            key={fieldIndex}
                            style={{ 
                              backgroundColor: '#dc3545', 
                              color: 'white', 
                              padding: '4px 8px', 
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                          >
                            {field}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Email Body */}
                  {log.email_body && (
                    <details style={{ marginTop: '8px' }}>
                      <summary style={{ 
                        cursor: 'pointer', 
                        fontWeight: '600', 
                        color: '#0066cc',
                        padding: '6px 0',
                        borderBottom: '1px solid #eee',
                        marginBottom: '8px'
                      }}>
                        📧 View Full Email Content
                      </summary>
                      <div 
                        style={{ 
                          marginTop: '8px', 
                          padding: '8px', 
                          backgroundColor: '#f8f8f8', 
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          maxHeight: '250px',
                          overflow: 'auto',
                          fontSize: '13px',
                          lineHeight: '1.4'
                        }}
                        dangerouslySetInnerHTML={{ __html: log.email_body }}
                      />
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <McButton 
          slot="primaryAction" 
          appearance="primary" 
          fit="medium" 
          dialogaction="ok"
          click={handleClose}
        >
          Close
        </McButton>
      </McModal>

      {notification && (
        <McNotification
          type={notification.type}
          body={notification.message}
          position="top-center"
          autoclose
          closable
        />
      )}
    </>
  );
};

export default EmailHistoryModal;